/**
 * Inline SVG weapon silhouettes for the kill feed.
 * Each SVG is a simple geometric silhouette (~48x24 viewBox), white fill, no stroke.
 */

import { WEAPONS, type WeaponId } from '../core/config';
import { escapeHtml } from './utils';

// ── Weapon SVG silhouettes ──

export const WEAPON_ICONS: Record<string, string> = {
  // Pistols — short barrel, grip, small body
  glock:
    '<svg viewBox="0 0 48 24" xmlns="http://www.w3.org/2000/svg"><path d="M4 10h24l4-2h6v4h-6l-2 2v6h-4v-6H4z" fill="white"/></svg>',
  usp:
    '<svg viewBox="0 0 48 24" xmlns="http://www.w3.org/2000/svg"><path d="M4 10h22l2-1h8l2 1v4H36l-2 1v6h-4v-6l-2-1H4z" fill="white"/></svg>',
  p250:
    '<svg viewBox="0 0 48 24" xmlns="http://www.w3.org/2000/svg"><path d="M6 10h20l4-2h8v5h-8l-2 2v5h-4v-5l-1-1H6z" fill="white"/></svg>',
  deagle:
    '<svg viewBox="0 0 48 24" xmlns="http://www.w3.org/2000/svg"><path d="M2 9h26l4-2h10v6H32l-2 2v7h-6v-7H2z" fill="white"/></svg>',

  // Rifles — long barrel, stock, magazine
  ak47:
    '<svg viewBox="0 0 48 24" xmlns="http://www.w3.org/2000/svg"><path d="M0 10h36l4-2h6v4H38v2h-4v6h-3v-6h-4l-2 2H10l-4 2H0z" fill="white"/></svg>',
  m4a4:
    '<svg viewBox="0 0 48 24" xmlns="http://www.w3.org/2000/svg"><path d="M0 10h34l4-2h8v4H40l-2 1h-4v5h-3v-5h-6l-2 2H8l-4 2H0z" fill="white"/></svg>',
  famas:
    '<svg viewBox="0 0 48 24" xmlns="http://www.w3.org/2000/svg"><path d="M2 10h30l4-2h8v4h-8l-2 1v5h-3v-5h-4l-2 2H10l-4 2H2z" fill="white"/></svg>',
  galil:
    '<svg viewBox="0 0 48 24" xmlns="http://www.w3.org/2000/svg"><path d="M1 10h32l4-2h7v4h-7l-2 1h-3v5h-3v-5h-5l-2 2H9l-4 2H1z" fill="white"/></svg>',

  // SMGs — medium barrel, compact body, magazine
  mp9:
    '<svg viewBox="0 0 48 24" xmlns="http://www.w3.org/2000/svg"><path d="M4 9h26l2-1h6v4h-6l-1 1v7h-4V13h-6l-1 1H8l-2 2H4z" fill="white"/><circle cx="29" cy="17" r="1" fill="black" opacity="0.85"/></svg>',
  mac10:
    '<svg viewBox="0 0 48 24" xmlns="http://www.w3.org/2000/svg"><path d="M6 9h22l2-1h8v4h-8l-1 1v7h-4V13h-4l-2 2H8l-2 1H4z" fill="white"/><circle cx="26" cy="16" r="0.9" fill="black" opacity="0.85"/><circle cx="26" cy="19" r="0.9" fill="black" opacity="0.85"/></svg>',

  // Shotguns — long wide barrel, pump grip
  nova:
    '<svg viewBox="0 0 48 24" xmlns="http://www.w3.org/2000/svg"><path d="M0 9h38l2-1h6v5H38v1h-4v6h-3v-6h-8l-2 2H8l-4 2H0z" fill="white"/></svg>',

  // Scoped rifles — rifle body with scope
  aug:
    '<svg viewBox="0 0 48 24" xmlns="http://www.w3.org/2000/svg"><path d="M2 11h30l2-2h6v2h-2v2h-4v2h-6v4h-3v-4H12l-2 2H4l-2 1z" fill="white"/><rect x="24" y="6" width="8" height="2" rx="1" fill="white"/></svg>',
  sg553:
    '<svg viewBox="0 0 48 24" xmlns="http://www.w3.org/2000/svg"><path d="M2 11h28l2-2h8v2h-2v2h-4v2h-6v4h-3v-4H12l-2 2H4l-2 1z" fill="white"/><rect x="22" y="6" width="8" height="2" rx="1" fill="white"/></svg>',

  // Silenced rifle — rifle with suppressor
  m4a1s:
    '<svg viewBox="0 0 48 24" xmlns="http://www.w3.org/2000/svg"><path d="M2 11h26l2-2h4v2h-2v2h-4v2h-4v4h-3v-4H10l-2 2H4l-2 1z" fill="white"/><rect x="28" y="10" width="14" height="4" rx="2" fill="white" opacity="0.6"/></svg>',

  // P90 — unique bullpup shape
  p90:
    '<svg viewBox="0 0 48 24" xmlns="http://www.w3.org/2000/svg"><path d="M4 10h24l4-2h4v4h-4v2h-6v6h-4v-6H10l-4 2H4z" fill="white"/><circle cx="23" cy="11" r="0.8" fill="black" opacity="0.85"/><circle cx="26" cy="11" r="0.8" fill="black" opacity="0.85"/><circle cx="29" cy="11" r="0.8" fill="black" opacity="0.85"/></svg>',

  // Additional pistols
  fiveseven:
    '<svg viewBox="0 0 48 24" xmlns="http://www.w3.org/2000/svg"><path d="M10 10h18l2-2h4v4h-4v2h-4v6h-4v-6h-8l-2 2h-2z" fill="white"/></svg>',
  tec9:
    '<svg viewBox="0 0 48 24" xmlns="http://www.w3.org/2000/svg"><path d="M6 10h22l2-2h4v4h-4v2h-6v6h-4v-6H10l-4 2H4z" fill="white"/><rect x="14" y="14" width="4" height="6" fill="white"/></svg>',

  // Snipers — very long barrel, scope on top, stock
  awp:
    '<svg viewBox="0 0 48 24" xmlns="http://www.w3.org/2000/svg"><path d="M0 11h40l2-3h4v2h-2v2h-4v2h-4v4h-3v-4h-8l-2 2H8l-4 2H0z" fill="white"/><rect x="30" y="6" width="8" height="3" rx="1" fill="white"/></svg>',
  ssg08:
    '<svg viewBox="0 0 48 24" xmlns="http://www.w3.org/2000/svg"><path d="M0 11h38l2-3h4v2h-2v2h-2v2h-4v4h-3v-4h-8l-2 2H8l-4 2H0z" fill="white"/><rect x="28" y="6" width="7" height="3" rx="1" fill="white"/></svg>',

  // Melee — blade shape + grip
  knife:
    '<svg viewBox="0 0 48 24" xmlns="http://www.w3.org/2000/svg"><path d="M4 10l20-2 8 4-8 4H16v4h-4v-4H4z" fill="white"/></svg>',

  // Grenades — round shape
  he_grenade:
    '<svg viewBox="0 0 48 24" xmlns="http://www.w3.org/2000/svg"><path d="M18 10h12l3 4v3a8 8 0 01-16 0v-3z" fill="white"/><rect x="21" y="4" width="6" height="5" rx="1" fill="white"/><path d="M18 14h14M20 18h10M22 10v12M27 10v12" fill="none" stroke="black" stroke-width="1" opacity="0.45"/></svg>',
  decoy_grenade:
    '<svg viewBox="0 0 48 24" xmlns="http://www.w3.org/2000/svg"><circle cx="24" cy="14" r="8" fill="white"/><rect x="21" y="2" width="6" height="5" rx="1" fill="white"/><path d="M27 4h5v3h-3V6h-2z" fill="white"/></svg>',
  smoke_grenade:
    '<svg viewBox="0 0 48 24" xmlns="http://www.w3.org/2000/svg"><rect x="13" y="10" width="22" height="9" rx="4" fill="white"/><rect x="17" y="7" width="14" height="4" rx="2" fill="white"/><circle cx="12" cy="8" r="2" fill="white" opacity="0.75"/><circle cx="36" cy="7" r="3" fill="white" opacity="0.6"/><path d="M18 14h12" stroke="black" stroke-width="1" opacity="0.35"/></svg>',
  flashbang:
    '<svg viewBox="0 0 48 24" xmlns="http://www.w3.org/2000/svg"><rect x="19" y="6" width="10" height="15" rx="2" fill="white"/><rect x="21" y="3" width="6" height="4" rx="1" fill="white"/><path d="M24 0v4M32 4l-3 3M16 4l3 3M14 12h4M30 12h4" stroke="white" stroke-width="2" stroke-linecap="round"/><path d="M21 11h6M21 16h6" stroke="black" stroke-width="1" opacity="0.4"/></svg>',
  molotov:
    '<svg viewBox="0 0 48 24" xmlns="http://www.w3.org/2000/svg"><path d="M20 8h8v2l2 4v6a4 4 0 01-4 4h-4a4 4 0 01-4-4v-6l2-4z" fill="white"/><rect x="22" y="2" width="4" height="6" rx="1" fill="white"/></svg>',
  incendiary:
    '<svg viewBox="0 0 48 24" xmlns="http://www.w3.org/2000/svg"><path d="M20 8h8v2l2 4v6a4 4 0 01-4 4h-4a4 4 0 01-4-4v-6l2-4z" fill="white"/><rect x="22" y="2" width="4" height="6" rx="1" fill="white"/></svg>',
};

/** Fallback SVG for unknown weapons — question mark for unmapped items. */
const FALLBACK_ICON =
  '<svg viewBox="0 0 48 24" xmlns="http://www.w3.org/2000/svg"><circle cx="24" cy="12" r="10" fill="white" opacity="0.16"/><text x="24" y="19" text-anchor="middle" font-family="Arial, sans-serif" font-size="20" font-weight="700" fill="white">?</text></svg>';

/** Small skull SVG for headshot indicator. */
export const SKULL_ICON_SVG =
  '<svg viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg"><path d="M8 1C4.7 1 2 3.5 2 6.5c0 2 1.1 3.7 2.8 4.6L5 14h2v-1h2v1h2l.2-2.9C12.9 10.2 14 8.5 14 6.5 14 3.5 11.3 1 8 1zM6 7.5a1.5 1.5 0 110-3 1.5 1.5 0 010 3zm4 0a1.5 1.5 0 110-3 1.5 1.5 0 010 3z" fill="white"/></svg>';

/**
 * Encode an SVG string into a data URI suitable for <img src>.
 */
function svgToDataUri(svg: string): string {
  return 'data:image/svg+xml,' + encodeURIComponent(svg);
}

/**
 * Get a data URI for a weapon icon by its ID.
 * Falls back to a generic icon if the weapon is not recognized.
 */
export function getWeaponIconDataUri(weaponId: string): string {
  const svg = WEAPON_ICONS[weaponId] ?? FALLBACK_ICON;
  return svgToDataUri(svg);
}

/**
 * Check if a weapon is a sniper type (for noscope indicator).
 */
function isSniperWeapon(weaponId: string): boolean {
  const weapon = WEAPONS[weaponId as WeaponId];
  return weapon?.type === 'sniper';
}

/**
 * Build the inner HTML for a kill feed entry.
 *
 * Layout: [killer] [weapon-icon] [headshot-skull?] [noscope?] [victim]
 *
 * @param killer - Killer's display name
 * @param victim - Victim's display name
 * @param weaponId - Weapon ID string (e.g. 'ak47', 'he_grenade')
 * @param headshot - Whether the kill was a headshot
 * @param noscope - Whether the kill was a noscope (only relevant for snipers)
 */
export function buildKillFeedEntryHTML(
  killer: string,
  victim: string,
  weaponId: string,
  headshot: boolean,
  noscope: boolean = false,
): string {
  const iconUri = getWeaponIconDataUri(weaponId);
  const skullUri = svgToDataUri(SKULL_ICON_SVG);

  let html = '';

  // Killer name
  html += `<span class="killfeed-killer">${escapeHtml(killer)}</span>`;

  // Weapon icon
  html += `<span class="killfeed-weapon"><img src="${iconUri}" width="32" height="16" alt="${escapeHtml(weaponId)}" /></span>`;

  // Headshot skull indicator
  if (headshot) {
    html += `<span class="killfeed-headshot"><img src="${skullUri}" width="14" height="14" alt="headshot" /></span>`;
  }

  // Noscope badge (only for sniper weapons)
  if (noscope && isSniperWeapon(weaponId)) {
    html += `<span class="killfeed-noscope">NS</span>`;
  }

  // Victim name
  html += `<span class="killfeed-victim">${escapeHtml(victim)}</span>`;

  return html;
}
